

import './editorModal.less';

const CLOSE_ICON =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';

let activeModalCount = 0;

/**
 * Opens a modal and returns a promise that resolves when it closes.
 *
 * @param {Object} options
 * @param {string} [options.title]
 * @param {HTMLElement|string} [options.content]
 * @param {number} [options.width=560]
 * @param {string} [options.confirmText='Save']
 * @param {string} [options.cancelText='Cancel']
 * @param {boolean} [options.showConfirm=true]
 * @param {boolean} [options.showCancel=true]
 * @param {boolean} [options.showClose=true]
 * @param {boolean} [options.closeOnBackdrop=true]
 * @param {boolean} [options.closeOnEscape=true]
 * @param {string} [options.confirmVariant='primary'] - primary | danger
 * @param {Array<Object>} [options.footerButtons] - fully custom footer buttons.
 *        Each: { text, variant, action, value, autoClose=true, onClick }
 * @param {(value:any)=>(boolean|Promise<boolean>)} [options.onConfirm]
 *        Return false to keep the modal open (e.g. validation failed).
 * @param {(close:(result?:any)=>void)=>void} [options.onReady]
 *        Receives a programmatic close function once the modal is mounted.
 * @returns {Promise<{confirmed: boolean, action: string, value: any}>}
 */
export const openEditorModal = (options = {}) => {
    const {
        title = '',
        content = '',
        width = 560,
        confirmText = 'Save',
        cancelText = 'Cancel',
        showConfirm = true,
        showCancel = true,
        showClose = true,
        closeOnBackdrop = true,
        closeOnEscape = true,
        confirmVariant = 'primary',
        footerButtons = null,
        extraFooterButtons = null,
        onConfirm = null,
        onReady = null,
    } = options;

    return new Promise((resolve) => {
        let settled = false;
        const previouslyFocused = document.activeElement;

        const overlay = document.createElement('div');
        overlay.className = 'sio-modal-overlay';

        const modal = document.createElement('div');
        modal.className = 'sio-modal';
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        modal.style.width = `${width}px`;
        if (title) modal.setAttribute('aria-label', title);

        // Header
        const header = document.createElement('div');
        header.className = 'sio-modal__header';
        const titleEl = document.createElement('h2');
        titleEl.className = 'sio-modal__title';
        titleEl.textContent = title;
        header.appendChild(titleEl);

        if (showClose) {
            const closeBtn = document.createElement('button');
            closeBtn.type = 'button';
            closeBtn.className = 'sio-modal__close';
            closeBtn.setAttribute('aria-label', 'Close');
            closeBtn.innerHTML = CLOSE_ICON;
            closeBtn.addEventListener('click', () =>
                close({ confirmed: false, action: 'close', value: null })
            );
            header.appendChild(closeBtn);
        }

        // Body
        const body = document.createElement('div');
        body.className = 'sio-modal__body';
        if (typeof content === 'string') {
            body.innerHTML = content;
        } else if (content instanceof HTMLElement) {
            body.appendChild(content);
        }

        // Footer
        const footer = document.createElement('div');
        footer.className = 'sio-modal__footer';

        const close = (result) => {
            if (settled) return;
            settled = true;
            overlay.classList.remove('sio-modal-overlay--open');
            document.removeEventListener('keydown', onKeyDown, true);
            activeModalCount = Math.max(0, activeModalCount - 1);
            if (activeModalCount === 0) {
                document.body.style.removeProperty('overflow');
            }
            const cleanup = () => {
                overlay.remove();
                if (
                    previouslyFocused &&
                    typeof previouslyFocused.focus === 'function'
                ) {
                    previouslyFocused.focus();
                }
            };
            overlay.addEventListener('transitionend', cleanup, { once: true });
            setTimeout(cleanup, 250);
            resolve(result);
        };

        const runConfirm = async (action = 'confirm', value = null) => {
            if (typeof onConfirm === 'function') {
                const ok = await onConfirm(value);
                if (ok === false) return;
            }
            close({ confirmed: true, action, value });
        };

        if (Array.isArray(footerButtons) && footerButtons.length) {
            footerButtons.forEach((cfg) => {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = `sio-modal__btn sio-modal__btn--${cfg.variant || 'secondary'}`;
                btn.textContent = cfg.text || '';
                btn.addEventListener('click', async () => {
                    if (typeof cfg.onClick === 'function') {
                        const keepOpen = await cfg.onClick();
                        if (keepOpen === false) return;
                    }
                    if (cfg.autoClose === false) return;
                    close({
                        confirmed: !!cfg.confirmed,
                        action: cfg.action || cfg.text || 'button',
                        value: cfg.value ?? null,
                    });
                });
                footer.appendChild(btn);
            });
        } else {
            if (Array.isArray(extraFooterButtons) && extraFooterButtons.length) {
                extraFooterButtons.forEach((cfg, index) => {
                    const btn = document.createElement('button');
                    btn.type = 'button';
                    btn.className = `sio-modal__btn sio-modal__btn--${cfg.variant || 'secondary'}`;
                    if (index === 0) {
                        btn.classList.add('sio-modal__btn--start');
                    }
                    btn.textContent = cfg.text || '';
                    btn.addEventListener('click', async () => {
                        if (typeof cfg.onClick === 'function') {
                            const keepOpen = await cfg.onClick(close);
                            if (keepOpen === false) return;
                        }
                        if (cfg.autoClose === false) return;
                        close({
                            confirmed: !!cfg.confirmed,
                            action: cfg.action || cfg.text || 'button',
                            value: cfg.value ?? null,
                        });
                    });
                    footer.appendChild(btn);
                });
            }
            if (showCancel) {
                const cancelBtn = document.createElement('button');
                cancelBtn.type = 'button';
                cancelBtn.className =
                    'sio-modal__btn sio-modal__btn--secondary';
                cancelBtn.textContent = cancelText;
                cancelBtn.addEventListener('click', () =>
                    close({ confirmed: false, action: 'cancel', value: null })
                );
                footer.appendChild(cancelBtn);
            }
            if (showConfirm) {
                const confirmBtn = document.createElement('button');
                confirmBtn.type = 'button';
                confirmBtn.className = `sio-modal__btn sio-modal__btn--${confirmVariant}`;
                confirmBtn.textContent = confirmText;
                confirmBtn.addEventListener('click', () => runConfirm());
                footer.appendChild(confirmBtn);
            }
        }

        modal.appendChild(header);
        modal.appendChild(body);
        if (footer.childElementCount) modal.appendChild(footer);
        overlay.appendChild(modal);

        if (closeOnBackdrop) {
            overlay.addEventListener('mousedown', (event) => {
                if (event.target === overlay) {
                    close({ confirmed: false, action: 'backdrop', value: null });
                }
            });
        }

        const getFocusable = () =>
            Array.from(
                modal.querySelectorAll(
                    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
                )
            ).filter((el) => el.offsetParent !== null);

        const onKeyDown = (event) => {
            if (event.key === 'Escape' && closeOnEscape) {
                event.preventDefault();
                close({ confirmed: false, action: 'escape', value: null });
                return;
            }
            if (event.key === 'Tab') {
                const focusable = getFocusable();
                if (!focusable.length) return;
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault();
                    last.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        };

        document.addEventListener('keydown', onKeyDown, true);
        document.body.appendChild(overlay);
        activeModalCount += 1;
        document.body.style.overflow = 'hidden';

        // Trigger enter animation + initial focus
        requestAnimationFrame(() => {
            overlay.classList.add('sio-modal-overlay--open');
            const focusable = getFocusable();
            const target =
                modal.querySelector('.sio-modal__btn--primary') ||
                focusable[0];
            target?.focus?.();
        });

        if (typeof onReady === 'function') {
            onReady((result) =>
                close(
                    result || {
                        confirmed: false,
                        action: 'programmatic',
                        value: null,
                    }
                )
            );
        }
    });
};

export default openEditorModal;
